<template>
  <div class="admin-page">
    <!-- 登录页面 -->
    <div v-if="!isLoggedIn" class="login-container">
      <div class="login-box">
        <h2>工作人员登录</h2>
        <van-cell-group inset>
          <van-field
            v-model="loginForm.username"
            label="用户名"
            placeholder="请输入用户名"
          />
          <van-field
            v-model="loginForm.password"
            type="password"
            label="密码"
            placeholder="请输入密码"
          />
        </van-cell-group>
        <div class="login-btn">
          <van-button type="primary" block round :loading="loginLoading" @click="handleLogin">
            登录
          </van-button>
        </div>
      </div>
    </div>

    <!-- 核销后台 -->
    <div v-else class="checkin-container">
      <van-nav-bar title="核销后台" />
      
      <!-- 统计卡片 -->
      <div class="stats-cards">
        <div class="stat-card">
          <div class="stat-number">{{ statistics.totalCheckin || 0 }}</div>
          <div class="stat-label">已核销</div>
        </div>
        <div class="stat-card">
          <div class="stat-number">{{ statistics.totalRegister || 0 }}</div>
          <div class="stat-label">总报名</div>
        </div>
      </div>

      <!-- 扫码核销区域 -->
      <div class="scan-section">
        <h3>扫码核销</h3>
        <!-- 微信配置加载中 -->
        <div v-if="!wxConfigReady" class="scan-loading">
          <van-loading type="spinner" size="40px" />
          <p>正在加载微信配置...</p>
        </div>
        <!-- 扫码按钮 -->
        <div v-else class="scan-btn-wrapper">
          <van-button 
            type="primary" 
            size="large" 
            block 
            round
            :loading="scanning"
            @click="startWechatScan"
          >
            <template #icon>
              <van-icon name="scan" size="24" />
            </template>
            点击开始扫码
          </van-button>
        </div>
        
        <div class="scan-tip">
          <p>请使用微信扫一扫功能扫描用户的核销二维码</p>
        </div>
      </div>

      <!-- 核销结果 -->
      <div class="result-section" v-if="scanResult">
        <van-cell-group inset>
          <van-cell 
            :title="scanResult.message" 
            :label="scanResult.description"
            :class="['result-cell', scanResult.type]"
          >
            <template #icon>
              <van-icon 
                :name="scanResult.type === 'success' ? 'success' : 'warning-o'" 
                :color="scanResult.type === 'success' ? '#07c160' : '#ff976a'"
                size="24"
                style="margin-right: 10px;"
              />
            </template>
          </van-cell>
        </van-cell-group>
      </div>

      <!-- 最近核销记录 -->
      <div class="recent-section" v-if="recentCheckins.length > 0">
        <h3>最近核销记录</h3>
        <van-cell-group inset>
          <van-cell 
            v-for="(item, index) in recentCheckins" 
            :key="index"
            :title="maskPhone(item.phone)"
            :label="formatTime(item.checkinTime)"
          >
            <template #right-icon>
              <van-tag type="success">已核销</van-tag>
            </template>
          </van-cell>
        </van-cell-group>
      </div>

      <!-- 底部操作栏 -->
      <div class="bottom-bar">
        <van-button type="default" block @click="logout">退出登录</van-button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue'
import { showSuccessToast, showFailToast } from '@/utils/request'
import wx from 'weixin-js-sdk'
import { getWechatJsConfig } from '@/api/wechat'

export default {
  name: 'AdminPage',
  setup() {
    const isLoggedIn = ref(false)
    const loginLoading = ref(false)
    const scanning = ref(false)
    const scanResult = ref(null)
    const statistics = ref({})
    const recentCheckins = ref([])
    const wxConfigReady = ref(false)

    const loginForm = reactive({
      username: '',
      password: ''
    })

    onMounted(() => {
      // 检查是否已登录
      const token = localStorage.getItem('adminToken')
      if (token) {
        isLoggedIn.value = true
        loadStatistics()
        initWechatConfig()
      }
    })

    // 初始化微信配置
    const initWechatConfig = async () => {
      try {
        const currentUrl = window.location.href.split('#')[0]
        const res = await getWechatJsConfig(currentUrl)
        if (res.code === 200 && res.data) {
          // 配置微信 JS-SDK
          wx.config({
            debug: false,
            appId: res.data.appId,
            timestamp: res.data.timestamp,
            nonceStr: res.data.nonceStr,
            signature: res.data.signature,
            jsApiList: ['scanQRCode']
          })

          wx.ready(() => {
            console.log('微信 JS-SDK 配置成功')
            wxConfigReady.value = true
          })

          wx.error((err) => {
            console.error('微信 JS-SDK 配置失败:', err)
            wxConfigReady.value = false
            showFailToast('微信配置失败，请刷新页面重试')
          })
        }
      } catch (error) {
        console.error('获取微信配置失败:', error)
        showFailToast('获取微信配置失败')
      }
    }

    // 登录
    const handleLogin = async () => {
      if (!loginForm.username || !loginForm.password) {
        showFailToast('请输入用户名和密码')
        return
      }

      loginLoading.value = true
      try {
        const response = await fetch('/api/admin/login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(loginForm)
        })
        const res = await response.json()

        if (res.code === 200) {
          localStorage.setItem('adminToken', res.data.token)
          localStorage.setItem('adminName', res.data.name)
          isLoggedIn.value = true
          showSuccessToast('登录成功')
          loadStatistics()
          initWechatConfig()
        } else {
          showFailToast(res.message || '登录失败')
        }
      } catch (error) {
        console.error(error)
        showFailToast('登录失败')
      } finally {
        loginLoading.value = false
      }
    }

    // 退出登录
    const logout = () => {
      localStorage.removeItem('adminToken')
      localStorage.removeItem('adminName')
      isLoggedIn.value = false
      scanResult.value = null
      recentCheckins.value = []
    }

    // 加载统计数据
    const loadStatistics = async () => {
      try {
        const response = await fetch('/api/admin/statistics', {
          headers: {
            'Authorization': localStorage.getItem('adminToken')
          }
        })
        const res = await response.json()
        if (res.code === 200) {
          statistics.value = res.data
        }
      } catch (error) {
        console.error(error)
      }
    }

    // 开始微信扫码
    const startWechatScan = () => {
      scanning.value = true
      scanResult.value = null

      wx.scanQRCode({
        needResult: 1, // 返回扫描结果
        scanType: ['qrCode'], // 只扫二维码
        success: (res) => {
          const result = res.resultStr
          if (result) {
            handleCheckin(result)
          } else {
            scanResult.value = {
              type: 'warning',
              message: '扫码失败',
              description: '未获取到二维码内容'
            }
            showFailToast('扫码失败')
          }
          scanning.value = false
        },
        fail: (err) => {
          console.error('微信扫码失败:', err)
          scanResult.value = {
            type: 'warning',
            message: '扫码失败',
            description: '请检查微信权限设置'
          }
          showFailToast('扫码失败')
          scanning.value = false
        },
        cancel: () => {
          // 用户取消扫码
          scanning.value = false
        }
      })
    }

    // 核销处理
    const handleCheckin = async (token) => {
      try {
        const response = await fetch('/api/admin/checkin', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': localStorage.getItem('adminToken')
          },
          body: JSON.stringify({
            mode: 'token',
            value: token
          })
        })
        
        const res = await response.json()
        
        if (res.code === 200) {
          scanResult.value = {
            type: 'success',
            message: '核销成功',
            description: `手机号：${maskPhone(res.data.phone)}`
          }
          showSuccessToast('核销成功')
          
          // 添加到最近核销记录
          recentCheckins.value.unshift({
            phone: res.data.phone,
            checkinTime: res.data.checkinTime
          })
          // 只保留最近 5 条
          if (recentCheckins.value.length > 5) {
            recentCheckins.value = recentCheckins.value.slice(0, 5)
          }
          
          loadStatistics()
        } else {
          scanResult.value = {
            type: 'warning',
            message: '核销失败',
            description: res.message
          }
          showFailToast(res.message)
        }
      } catch (error) {
        console.error('核销失败:', error)
        scanResult.value = {
          type: 'warning',
          message: '核销失败',
          description: '网络错误，请重试'
        }
        showFailToast('核销失败')
      }
    }

    const formatTime = (time) => {
      if (!time) return '-'
      return new Date(time).toLocaleString()
    }

    const maskPhone = (phone) => {
      if (!phone || phone.length < 7) return phone
      return phone.substring(0, 3) + '****' + phone.substring(7)
    }

    return {
      isLoggedIn,
      loginLoading,
      scanning,
      scanResult,
      statistics,
      recentCheckins,
      wxConfigReady,
      loginForm,
      handleLogin,
      logout,
      startWechatScan,
      formatTime,
      maskPhone
    }
  }
}
</script>

<style scoped>
.admin-page {
  min-height: 100vh;
  background: #f5f5f5;
}

.login-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  padding: 20px;
}

.login-box {
  width: 100%;
  max-width: 400px;
  background: #fff;
  border-radius: 12px;
  padding: 30px 20px;
}

.login-box h2 {
  text-align: center;
  margin-bottom: 30px;
  color: #333;
}

.login-btn {
  margin-top: 30px;
  padding: 0 16px;
}

.checkin-container {
  padding-bottom: 80px;
}

.stats-cards {
  display: flex;
  justify-content: space-around;
  padding: 20px;
  gap: 10px;
}

.stat-card {
  flex: 1;
  background: #fff;
  border-radius: 12px;
  padding: 20px 10px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.stat-number {
  font-size: 28px;
  font-weight: bold;
  color: #07c160;
  margin-bottom: 5px;
}

.stat-label {
  font-size: 13px;
  color: #666;
}

.scan-section {
  background: #fff;
  border-radius: 12px;
  padding: 25px 20px;
  margin: 0 15px 20px;
  text-align: center;
}

.scan-section h3 {
  margin-bottom: 25px;
  color: #333;
  font-size: 18px;
}

.scan-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  margin-bottom: 20px;
}

.scan-loading p {
  margin-top: 15px;
  color: #999;
  font-size: 14px;
}

.scan-btn-wrapper {
  margin-bottom: 20px;
}

.scan-tip {
  color: #999;
  font-size: 14px;
  line-height: 1.6;
}

.scan-tip p {
  margin: 0;
}

.result-section {
  margin: 0 15px 20px;
}

.result-cell {
  padding: 15px;
}

.result-cell.success {
  background: #f0f9f4;
}

.result-cell.warning {
  background: #fff5e6;
}

.recent-section {
  margin: 0 15px 20px;
}

.recent-section h3 {
  margin-bottom: 15px;
  padding: 0 5px;
  color: #333;
  font-size: 16px;
}

.bottom-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 10px 20px;
  background: #fff;
  border-top: 1px solid #eee;
}
</style>
